import { captureException } from '@sentry/react';
import { Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Input, Pagination, Space } from 'antd';
import { DeleteModal } from 'components/Dialog';
import useStateManager from 'components/hooks/useStateManager';
import Loading from 'components/Loading';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { isEmpty } from 'lodash';
import React, { memo, useMemo, useState } from 'react';
import { useCurrentUser } from 'store/users/selectors';
import styled from 'styled-components';
import { useQuotes } from 'utils/methods/quotes';
import AddEditQuotes from './addEditQuotes';
import QuoteComponentTable from './quoteComponentTable';
import { Add } from '@mui/icons-material';
import { useHasPermission } from 'utils/permissions';
import { message } from 'antd';
import SentryWrap from 'components/Sentry';
const { Search } = Input;

const tableHeader = [
  { id: 1, headerText: 'Sequence No.' },
  { id: 2, headerText: 'Quote' },
  { id: 3, headerText: 'Author' },
  { id: 4, headerText: 'Edit' },
  { id: 5, headerText: 'Delete' },
];

const QuoteModuleComponent = ({ quotesData, id }) => {
  const showAlert = useAlert();
  const { createQuotes, updateQuotes } = useQuotes();
  const currentUser = useCurrentUser();
  
  const hasViewQuotes = useHasPermission('quotes.view');
  const hasAddQuotes = useHasPermission('quotes.add');
  const hasEditQuotes = useHasPermission('quotes.edit');
  const hasDeleteQuotes = useHasPermission('quotes.delete');
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 30,
    total: 0,
    pageSizeOptions: ['30', '50', '100'],
  });
  const [states, updateState] = useStateManager({
    openQuotesModal: false,
    searchText: '',
    editQuote: { editIndex: null, isEdit: false, editData: {} },
    deleteQuote: { open: false, deleteIndex: null },
    loading: { value: '', loader: false },
  });

  const sortedQuotesData = useMemo(() => {
    return [...quotesData].sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at || 0);
      const dateB = new Date(b.updated_at || b.created_at || 0);
      return dateB - dateA;
    });
  }, [quotesData]);

  const filteredQuotes = useMemo(() => {
    const filtered = sortedQuotesData.filter((quote) => {
      if (!states.searchText) return true;

      const searchTerm = states?.searchText?.toLowerCase();
      return (
        quote.message?.toLowerCase().includes(searchTerm) ||
        quote.author?.toLowerCase().includes(searchTerm)
      );
    });

    setPagination((prev) => ({
      ...prev,
      total: filtered.length,
      current: 1,
    }));

    return filtered;
  }, [sortedQuotesData, states?.searchText]);

  const paginatedQuotes = useMemo(() => {
    const startIndex = (pagination.current - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredQuotes.slice(startIndex, endIndex);
  }, [filteredQuotes, pagination.current, pagination.pageSize]);

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  const handlePageSizeChange = (current, size) => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: size,
    }));
  };

  const handlerDeleteQuote = async (quoteIndex) => {
    try {
      updateState({ loading: { loader: true, value: 'delete' } });

      const quoteToDelete = paginatedQuotes[quoteIndex];

      const updatedData = quotesData.filter((quote) => {
        if (quote.id && quoteToDelete.id) {
          return quote.id !== quoteToDelete.id;
        }

        return !(quote.message === quoteToDelete.message && quote.author === quoteToDelete.author);
      });

      const payload = {
        data: JSON.stringify(updatedData),
        updated_at: new Date(),
        user: {
          role: currentUser.role,
          id: currentUser.id,
        },
      };

      const result = await updateQuotes(id, payload);

      if (result.success) {
        showAlert('Quote deleted successfully', 'success');

        const newFilteredTotal = filteredQuotes.length - 1;
        const maxPage = Math.ceil(newFilteredTotal / pagination.pageSize);
        if (pagination.current > maxPage && maxPage > 0) {
          setPagination((prev) => ({
            ...prev,
            current: maxPage,
          }));
        }
      } else {
        showAlert('Failed to delete quote', 'error');
      }
    } catch (error) {
      captureException(`659 Error listening to session changes: ${error}`);
      showAlert('Error deleting quote', 'error');
    } finally {
      updateState({
        deleteQuote: { open: false, deleteIndex: null },
        loading: { loader: false, value: '' },
      });
    }
  };

  const handlerEdit = (index) => {
    if (!hasEditQuotes) {
      message.warning('You do not have permission to access this feature.');
      return;
    }
    try {
      const quoteToEdit = paginatedQuotes[index];

      const actualIndex = quotesData.findIndex((quote) => {
        if (quote.id && quoteToEdit.id) {
          return quote.id === quoteToEdit.id;
        }

        return quote.message === quoteToEdit.message && quote.author === quoteToEdit.author;
      });

      updateState({
        openQuotesModal: true,
        editQuote: {
          isEdit: true,
          editIndex: actualIndex,
          editData: quoteToEdit,
        },
      });
    } catch (error) {
      captureException(`659 Error listening to session changes: ${error}`);
    }
  };

  const handlerDelete = (index) => {
    if (!hasDeleteQuotes) {
      message.warning('You do not have permission to access this feature.');
      return;
    }
    try {
      updateState({ deleteQuote: { open: true, deleteIndex: index } });
    } catch (error) {
      captureException(`659 Error listening to session changes: ${error}`);
    }
  };

  return (
    <SentryWrap>
      <AddEditQuotes
        open={states?.openQuotesModal}
        onClose={() =>
          updateState({
            openQuotesModal: false,
            editQuote: { editIndex: null, isEdit: false, editData: {} },
          })
        }
        id={id}
        createQuotes={createQuotes}
        quotesData={quotesData}
        edit={states?.editQuote}
      />
      <DeleteModal
        message={`Are you sure you want to delete "${
          paginatedQuotes[states?.deleteQuote?.deleteIndex]?.message
        }" quote?`}
        deleteToggle={states?.deleteQuote?.open}
        setDeleteToggle={() => {
          updateState({ deleteQuote: { open: false, deleteIndex: null } });
        }}
        loading={states?.loading?.loader && states?.loading?.value === 'delete'}
        onDelete={() => handlerDeleteQuote(states?.deleteQuote?.deleteIndex)}
        onClickCancel={() => updateState({ deleteQuote: { open: false, deleteIndex: null } })}
      />
      {hasViewQuotes && (
        <div className="w-full flex items-center justify-between flex-wrap">
          <h1>Quotes</h1>
          <div className="flex items-center gap-10px justify-end">
            <Space.Compact>
              <Search
                placeholder="Search Quotes and Authors"
                allowClear
                rootClassName="grouping-root"
                className="search-grouping"
                value={states?.searchText}
                onChange={(e) => updateState({ searchText: e.target.value })}
                style={{ width: '250px' }}
              />
            </Space.Compact>

            <Button
              onClick={() => {
                if (!hasAddQuotes) {
                  message.warning('You do not have permission to access this feature.');
                  return;
                }
                updateState({ openQuotesModal: true });
              }}
              variant="contained"
              color="primary"
              startIcon={<Add />}
            >
              Add
            </Button>
          </div>
        </div>
      )}

      {hasViewQuotes && (
        <StableWrapper>
          <Table
            style={{
              width: '100%',
              textAlign: 'center',
              backgroundColor: '#fff',
              marginTop: 20,
            }}
          >
            <TableHead>
              <TableRow>
                {tableHeader.map((item) => (
                  <StyledTableCell key={item.id}>{item?.headerText}</StyledTableCell>
                ))}
              </TableRow>
            </TableHead>

          {false ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <div style={{ padding: '20px 0' }}>
                    <Loading size={30} />
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {isEmpty(paginatedQuotes) && (
                <TableRow key={'0'} style={{ border: 0 }}>
                  <TableCell colSpan={hasEditQuotes && hasDeleteQuotes ? 5 : hasEditQuotes || hasDeleteQuotes ? 4 : 3} style={{ textAlign: 'center' }}>
                    {states.searchText ? 'No quotes found for your search' : 'No quotes available'}
                  </TableCell>
                </TableRow>
              )}

              {paginatedQuotes?.map((element, index) => (
                <QuoteComponentTable
                  key={element.id || `${element.message}-${index}`}
                  index={index}
                  sequence={(pagination.current - 1) * pagination.pageSize + index}
                  row={element}
                  handlerEdit={handlerEdit}
                  handleDelete={handlerDelete}
                  hasEditQuotes={hasEditQuotes}
                  hasDeleteQuotes={hasDeleteQuotes}
                />
              ))}

              {!isEmpty(filteredQuotes) && (
                <TableRow>
                  <TableCell colSpan={hasEditQuotes && hasDeleteQuotes ? 5 : hasEditQuotes || hasDeleteQuotes ? 4 : 3} align="right">
                    <Pagination
                      current={pagination.current}
                      pageSize={pagination.pageSize}
                      total={pagination.total}
                      onChange={handlePageChange}
                      showSizeChanger={true}
                      pageSizeOptions={pagination.pageSizeOptions}
                      onShowSizeChange={handlePageSizeChange}
                      showQuickJumper={true}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                      itemRender={(page, type, originalElement) => {
                        if (type === 'page' && page === pagination.current) {
                          return (
                            <div className="ant-pagination-item-active custom-active-page">
                              {page}
                            </div>
                          );
                        }
                        return originalElement;
                      }}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </StableWrapper>
      )}
    </SentryWrap>
  );
};

export default memo(QuoteModuleComponent);

const StableWrapper = styled.div``;

const StyledTableCell = styled(TableCell)`
  text-align: left;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
`;
